import {
  CapabilityManifestSchema,
  FocusBranchProjectionSchema,
  FocusEventPageQuerySchema,
  FocusEventPageSchema,
  FocusMessagePageQuerySchema,
  FocusMessagePageSchema,
  FocusQueryDecisionSchema,
  FocusRetryCommandSchema,
  FocusRunDetailSchema,
  FocusRunProjectionSchema,
  FocusRunSubmitSchema,
  FocusSourceProjectionSchema,
  FocusThreadCreateSchema,
  FocusThreadDetailSchema,
  FocusThreadListQuerySchema,
  FocusThreadListSchema,
  FocusThreadProjectionSchema,
  FocusThreadSearchCommandSchema,
  ProposalDecisionSchema,
  ProposalProjectionSchema,
  TaskScopedListSchema,
  type CapabilityManifest,
  type FocusEventPage,
  type FocusMessagePage,
  type FocusQueryDecision,
  type FocusRunDetail,
  type FocusRunProjection,
  type FocusRunSubmit,
  type FocusSourceProjection,
  type FocusThreadCreate,
  type FocusThreadDetail,
  type FocusThreadList,
  type ProposalProjection,
  type TaskScopedList,
} from "@mind/contracts/browser";
import { z } from "zod";
import { apiRequest, type ApiRequestOptions } from "../../api/client.js";
const FocusThreadCreatedSchema = z.object({
  thread: FocusThreadProjectionSchema,
  branch: FocusBranchProjectionSchema,
}).strict();

export type FocusThreadCreated = z.infer<typeof FocusThreadCreatedSchema>;
type FocusRetryCommand = z.infer<typeof FocusRetryCommandSchema>;

function pathWithQuery(path: string, parameters: Readonly<Record<string, string | number | null>>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(parameters)) {
    if (value !== null) query.set(key, String(value));
  }
  const encoded = query.toString();
  return encoded.length === 0 ? path : `${path}?${encoded}`;
}
function withOptionalSignal(options: ApiRequestOptions, signal?: AbortSignal): ApiRequestOptions {
  return signal === undefined ? options : { ...options, signal };
}

export function createQueryDecisionBody(input: FocusQueryDecision): FocusQueryDecision {
  return FocusQueryDecisionSchema.parse(input);
}

export const focusApi = {
  listThreads(
    workspaceId: string,
    cursor: string | null,
    limit: number,
    signal?: AbortSignal,
  ): Promise<FocusThreadList> {
    const input = FocusThreadListQuerySchema.parse({ workspaceId, cursor, limit });
    return apiRequest(FocusThreadListSchema, pathWithQuery("/api/focus/threads", {
      workspaceId: input.workspaceId,
      cursor: input.cursor,
      limit: input.limit,
    }), withOptionalSignal({}, signal));
  },

  searchThreads(
    workspaceId: string,
    query: string,
    cursor: string | null,
    limit: number,
    signal?: AbortSignal,
  ): Promise<FocusThreadList> {
    const body = FocusThreadSearchCommandSchema.parse({ workspaceId, query, cursor, limit });
    return apiRequest(FocusThreadListSchema, "/api/focus/threads/search", withOptionalSignal({
      method: "POST",
      body,
    }, signal));
  },

  createThread(input: FocusThreadCreate): Promise<FocusThreadCreated> {
    return apiRequest(FocusThreadCreatedSchema, "/api/focus/threads", {
      method: "POST",
      body: FocusThreadCreateSchema.parse(input),
    });
  },

  getThread(threadId: string, signal?: AbortSignal): Promise<FocusThreadDetail> {
    return apiRequest(FocusThreadDetailSchema, `/api/focus/threads/${encodeURIComponent(threadId)}`, withOptionalSignal({}, signal));
  },

  getMessages(
    threadId: string,
    branchId: string,
    afterSequence: string,
    limit: number,
    signal?: AbortSignal,
  ): Promise<FocusMessagePage> {
    const input = FocusMessagePageQuerySchema.parse({ branchId, afterSequence, limit });
    return apiRequest(FocusMessagePageSchema, pathWithQuery(
      `/api/focus/threads/${encodeURIComponent(threadId)}/messages`,
      { branchId: input.branchId, afterSequence: input.afterSequence, limit: input.limit },
    ), withOptionalSignal({}, signal));
  },

  registerManifest(
    workspaceId: string,
    manifest: CapabilityManifest,
    expiresAt: string,
  ): Promise<CapabilityManifest> {
    return apiRequest(CapabilityManifestSchema, "/api/focus/manifests", {
      method: "POST",
      body: { workspaceId, manifest: CapabilityManifestSchema.parse(manifest), expiresAt },
    });
  },

  submitRun(input: FocusRunSubmit): Promise<FocusRunProjection> {
    return apiRequest(FocusRunProjectionSchema, "/api/focus/runs", {
      method: "POST",
      body: FocusRunSubmitSchema.parse(input),
    });
  },

  getRun(runId: string, signal?: AbortSignal): Promise<FocusRunDetail> {
    return apiRequest(FocusRunDetailSchema, `/api/focus/runs/${encodeURIComponent(runId)}`, withOptionalSignal({}, signal));
  },

  getEvents(
    runId: string,
    afterSequence: string,
    limit: number,
    signal?: AbortSignal,
  ): Promise<FocusEventPage> {
    const input = FocusEventPageQuerySchema.parse({ afterSequence, limit });
    return apiRequest(FocusEventPageSchema, pathWithQuery(
      `/api/focus/runs/${encodeURIComponent(runId)}/events`,
      { afterSequence: input.afterSequence, limit: input.limit },
    ), withOptionalSignal({}, signal));
  },

  decideQuery(runId: string, decision: FocusQueryDecision): Promise<FocusRunProjection> {
    const body = createQueryDecisionBody(decision);
    return apiRequest(
      FocusRunProjectionSchema,
      `/api/focus/runs/${encodeURIComponent(runId)}/queries/${body.decision}`,
      { method: "POST", body },
    );
  },

  cancelRun(runId: string): Promise<FocusRunProjection> {
    return apiRequest(FocusRunProjectionSchema, `/api/focus/runs/${encodeURIComponent(runId)}/cancel`, {
      method: "POST",
      body: {},
    });
  },

  retryRun(sourceRunId: string, input: FocusRetryCommand): Promise<FocusRunProjection> {
    return apiRequest(FocusRunProjectionSchema, `/api/focus/runs/${encodeURIComponent(sourceRunId)}/retry`, {
      method: "POST",
      body: FocusRetryCommandSchema.parse(input),
    });
  },

  getSource(sourceId: string, signal?: AbortSignal): Promise<FocusSourceProjection> {
    return apiRequest(FocusSourceProjectionSchema, `/api/focus/sources/${encodeURIComponent(sourceId)}`, withOptionalSignal({}, signal));
  },

  getContextTasks(workspaceId: string, signal?: AbortSignal): Promise<TaskScopedList> {
    return apiRequest(TaskScopedListSchema, pathWithQuery("/api/tasks", {
      workspaceId,
      limit: 50,
      includeArchived: "false",
    }), withOptionalSignal({}, signal));
  },

  getProposal(proposalId: string, signal?: AbortSignal): Promise<ProposalProjection> {
    return apiRequest(ProposalProjectionSchema, `/api/proposals/${encodeURIComponent(proposalId)}`, withOptionalSignal({}, signal));
  },

  decideProposal(proposal: ProposalProjection, action: "accept" | "reject"): Promise<ProposalProjection> {
    const body = ProposalDecisionSchema.parse({
      commandId: crypto.randomUUID(),
      proposalId: proposal.id,
      action,
      proposalRevision: proposal.revision,
    });
    return apiRequest(ProposalProjectionSchema, `/api/proposals/${encodeURIComponent(proposal.id)}/${action}`, {
      method: "POST",
      body,
    });
  },
};
